<template>
    <div class="flex items-center justify-between space-x-1 px-3 py-2.5 bg-gray-800 rounded-md shadow ring-1 ring-white/15">
        <div class="flex items-center space-x-2.5 overflow-hidden">
            <Avatar
                size="sm"
                class="shrink-0"
                rounded="rounded-full border border-white/15"
                :chip-color="
                    call.status === CallStatus.new 
                    ? 'rose'
                    : call.status === CallStatus.active 
                    ? 'blue' 
                    : call.status === CallStatus.onHold 
                    ? 'amber' 
                    : undefined
                "
                :ui="{ size: { sm: 'text-xs' } }"
                :path="call.customer?.avatar" 
                :placeholder="[call.customer.first_name, call.customer.last_name].filter(Boolean).join(' ')" 
            />
            <div class="flex-1 overflow-hidden leading-none">
                <h2 class="font-medium text-sm text-white truncate">
                    {{ [call.customer.first_name, call.customer.middle_name, call.customer.last_name].filter(Boolean).join(' ') }}
                </h2>
                <div v-if="call.status === CallStatus.onHold && duration" class="flex items-center space-x-1.5">
                    <BadgePriority 
                        v-if="call.metadata?.priority" 
                        small 
                        soft 
                        prefix="Hold" 
                        :priority="call.metadata.priority"
                    />
                    <span class="-mt-px text-[13px] text-gray-300 font-mono">
                        {{ duration }}
                    </span>
                </div>
                <span v-else class="text-xs text-gray-400 capitalize">
                    {{ [call.customer.gender, age(call.customer.birth_date), call.customer.blood_type].filter(Boolean).join(' · ') }}
                </span>
            </div>
        </div>
        <template v-if="!isAdmin">
            <UButton
                v-if="callCenterState.activeCallId == call.id"
                @click="joinCall(call)"
                label="Open"
                size="xs"
                class="dark:text-white dark:bg-blue-500 dark:hover:bg-blue-600"
            />
            <UButton
                v-if="!callCenterState.activeCallId"
                @click="joinCall(call)"
                size="xs"
                label="Join"
                class="dark:text-white"
                :class="{
                    'dark:bg-rose-500 dark:hover:bg-rose-600': call.status === CallStatus.new, 
                    'dark:bg-blue-500 dark:hover:bg-blue-600': call.status === CallStatus.active, 
                    'dark:bg-amber-500 dark:hover:bg-amber-600': call.status === CallStatus.onHold,   
                }"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { CallStatus, type Call } from '~/types/call'
import { UserRole } from '~/types/user'

const props = defineProps<{
    call: Call
}>()

const auth = useAuthStore()

const { age } = useNumber()
const { state: callCenterState, joinCall } = useCallCenter()

const duration = ref<string>()
const durationTimer = ref<NodeJS.Timeout>()

const isAdmin = computed(() => auth.user?.role === UserRole.admin)

onMounted(() => {
    if (props.call.status === CallStatus.onHold && props.call?.on_hold_at) {
        setDuration()
        durationTimer.value = setInterval(() => setDuration(), 1000)
    }
})

onUnmounted(() => {
    if (durationTimer.value) {
        clearInterval(durationTimer.value)
    }
})

watch(() => props.call.status, (status) => {
    if (status === CallStatus.onHold && props.call?.on_hold_at) {
        setDuration()
        durationTimer.value = setInterval(() => setDuration(), 1000)
    }
})

function setDuration() {
    duration.value = dayjs().startOf('day').add(dayjs().diff(props.call?.on_hold_at, 'seconds'), 'seconds').format('mm:ss')
}
</script>